'use client'

import {useState} from 'react'
import {useTranslation} from '../../../hooks/useTranslation'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {GuestCheckoutError, previewEventGuestCheckout, type GuestCheckoutErrorCode, type GuestCheckoutPreview} from '../../../services/guest-checkout.service'
import {trackGuestCheckoutRejected, trackGuestCheckoutStarted} from '../../../lib/guest-checkout-journey'
import {benefitCardKey} from '@fitro/customer-ui/BenefitProvider'
import type {GuestBookingFailure} from './PublicGuestBookingChooser'

// The step still open to a visitor whose guest preview was refused (see GuestBookingFailure).
const PREVIEW_FAILURE_NEXT_STEPS: Partial<Record<GuestCheckoutErrorCode, GuestBookingFailure['next']>> = {
    login_required: 'sign_in',
    not_found: 'sign_in',
    full: 'other_terms',
    registration_closed: 'other_terms',
}

/**
 * The Guest Checkout of one Term up to its form: the benefit card the guest declares, the preview
 * that opens the form (and renews it), and why a preview sent the guest back to the choice.
 */
export function useEventGuestCheckout(event: PublicScheduleEvent, partnerSlug: string | undefined) {
    const {t} = useTranslation('landing')
    const [preview, setPreview] = useState<GuestCheckoutPreview | null>(null)
    const [previewing, setPreviewing] = useState(false)
    const [failure, setFailure] = useState<GuestBookingFailure | null>(null)
    // Key (`provider` or `provider:variant`) of the benefit card the guest will show on site; null books at the regular price.
    const [cardKey, setCardKey] = useState<string | null>(null)
    const cards = event.guestCheckoutPrice?.benefitCards ?? []

    const selectedCard = () => cards.find(card => benefitCardKey(card) === cardKey) ?? null

    // Why a preview was refused, and the step still open to the guest.
    function refusalOf(error: unknown): {code: GuestCheckoutErrorCode; failure: GuestBookingFailure} {
        const code = error instanceof GuestCheckoutError ? error.code : 'unavailable'
        const waitlist = code === 'full' && event.waitlistAvailable === true
        return {code, failure: {
            message: t(waitlist ? 'publicEntry.guestCheckout.fullWaitlist' : `publicEntry.guestCheckout.errors.${code}`),
            next: PREVIEW_FAILURE_NEXT_STEPS[code] ?? 'retry',
            waitlist,
        }}
    }

    async function start() {
        if (!partnerSlug || !event.publicCode) return
        setPreviewing(true)
        setFailure(null)
        try {
            setPreview(await previewEventGuestCheckout(partnerSlug, event.publicCode, selectedCard()))
            trackGuestCheckoutStarted()
        } catch (error) {
            trackGuestCheckoutRejected(error)
            const {code, failure} = refusalOf(error)
            if (code === 'benefit_card_unavailable') setCardKey(null)
            setFailure(failure)
        } finally {
            setPreviewing(false)
        }
    }

    /**
     * A new preview of the same choice for the open form, after the previous one ran out or changed;
     * the form stays while it loads. Returns why none could be made. A card price no longer on offer
     * goes back to the choice, where the guest picks the price again.
     */
    async function renew(): Promise<GuestBookingFailure | null> {
        if (!partnerSlug || !event.publicCode) return null
        try {
            setPreview(await previewEventGuestCheckout(partnerSlug, event.publicCode, selectedCard()))
            trackGuestCheckoutStarted()
            return null
        } catch (error) {
            trackGuestCheckoutRejected(error)
            const {code, failure} = refusalOf(error)
            if (code === 'benefit_card_unavailable') {
                setCardKey(null)
                cancel(failure.message)
            }
            return failure
        }
    }

    /** Back to the choice; a message explains why the guest has to start the booking again. */
    function cancel(message?: string) {
        setPreview(null)
        setFailure(message ? {message, next: 'retry'} : null)
    }

    return {cancel, cardKey, cards, failure, preview, previewing, renew, setCardKey, start}
}
